import { useCallback, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { Plan, PlanStarter, Program } from "@jackedlogs/shared";
import { CreateHub, CreateHubActions, resolveTheme } from "@jackedlogs/ui";

import { CreateHubLibraryWeb } from "./CreateHubLibraryWeb";
import { useThemePalette } from "./theme";

// Web glue for the shared Create-hub (HR-S4w) — the sidebar "Crea" CTA's
// twin of mobile's useCreateHubNav. Owns the hub open/close state and
// translates hub intents into composer requests AppShell routes to the Plan
// or Workouts surface (state-based nav — no deep links on web). RW-18: "New
// workout" now opens the lifted Logger via WorkoutsScreen's own composer-
// request handling, the same pattern the Plan surface already used for
// program/plan requests. "Repeat workout" stays unwired — CreateHubActions
// renders only the handlers it receives (capability gating by omission).

export type ComposerRequest =
  | { kind: "program"; program?: Program }
  | { kind: "plan"; plan?: Plan }
  // RW-18: the logger is a web surface now — "New workout" opens it straight
  // from the hub, same as mobile's useCreateHubNav. "Repeat workout" stays
  // unwired (onRepeatWorkout omitted below): it needs the full past-session
  // Workout + patternsBySlug hydrate the web console doesn't fetch yet, a
  // separate follow-up. `planStarter` (added for the Plan-tab "Start"
  // wiring, see useWorkoutStarter.ts) opens the Logger PRE-POPULATED from a
  // program session instead of blank — omitted for a plain "New workout".
  | { kind: "workout"; planStarter?: PlanStarter };

export function useCreateHubWeb(onComposer: (req: ComposerRequest) => void): {
  openHub: () => void;
  closeHub: () => void;
  hubNode: ReactElement;
} {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const openHub = useCallback(() => setVisible(true), []);
  const closeHub = useCallback(() => setVisible(false), []);

  const route = useCallback(
    (req: ComposerRequest) => {
      setVisible(false);
      onComposer(req);
    },
    [onComposer],
  );

  const hubNode = (
    <CreateHub
      theme={theme}
      visible={visible}
      onClose={closeHub}
      title={t("createHub.title", { defaultValue: "Create" })}
      closeLabel={t("createHub.close", { defaultValue: "Close" })}
    >
      <CreateHubActions
        theme={theme}
        onNewWorkout={() => route({ kind: "workout" })}
        onPlanProgram={() => route({ kind: "program" })}
        onPlanWorkout={() => route({ kind: "plan" })}
      />
      <CreateHubLibraryWeb
        theme={theme}
        dark={dark}
        onEditProgram={(program) => route({ kind: "program", program })}
        onEditPlan={(plan) => route({ kind: "plan", plan })}
        onStarter={(starter) => route({ kind: "workout", planStarter: starter })}
      />
    </CreateHub>
  );

  return { openHub, closeHub, hubNode };
}
